import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Header from './Header'; // Import Header
import Footer from './Footer'; // Import Footer
import '../styles.css';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Pagination state
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  // Pagination logic
  const totalPages = useMemo(() => Math.ceil(users.length / itemsPerPage), [users.length, itemsPerPage]);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return users.slice(startIndex, endIndex);
  }, [users, currentPage, itemsPerPage]);

  // Fetch users data
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const usersResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(usersResponse.data);
      setLoading(false);
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Error fetching data: ' + (err.response ? err.response.data.error : err.message));
      setLoading(false);
      if (err.response?.status === 403) {
        navigate('/login');
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatNumber = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const handleGoToPage = (e) => {
    const page = parseInt(e.target.value, 10);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) return <div className="content-container container my-4 text-center">Loading...</div>;
  if (error) return <div className="content-container container my-4 text-center text-danger">{error}</div>;

  return (
    <div className="page-container">
      <Header />
      <section className="content-container container my-4">
        <h2 className="text-center mb-4 text-white">Admin Dashboard - Users Overview</h2>

        {/* Users Table */}
        <h3 className="text-white">All Users</h3>
        <Paper className="mb-4">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Wallet Address</TableCell>
                <TableCell>Tokens Purchased</TableCell>
                <TableCell>Tokens Staked</TableCell>
                <TableCell>Tokens Locked</TableCell>
                <TableCell>Referral Rewards</TableCell>
                <TableCell>Is Admin</TableCell>
                <TableCell>Is Verified</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 ? (
                <TableRow><TableCell colSpan="10" align="center">No users found</TableCell></TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>{user.user_id}</TableCell>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.wallet_address || 'N/A'}</TableCell>
                    <TableCell>{formatNumber(user.total_tokens_purchased || 0)}</TableCell>
                    <TableCell>{formatNumber(user.total_staked || 0)}</TableCell>
                    <TableCell>{formatNumber(user.total_locked || 0)}</TableCell>
                    <TableCell>{formatNumber(user.total_referral_reward_tokens || 0)}</TableCell>
                    <TableCell>{user.is_admin ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{user.is_verified ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* Pagination Controls */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <FormControl variant="outlined" style={{ minWidth: '100px' }}>
            <InputLabel sx={{ color: 'white' }}>Page</InputLabel>
            <Select
              value={currentPage}
              onChange={handleGoToPage}
              label="Page"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '& .MuiSvgIcon-root': { color: 'white' },
              }}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <MenuItem key={page} value={page}>{page}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Go to Page"
            variant="outlined"
            type="number"
            value=""
            onChange={(e) => {
              const page = parseInt(e.target.value, 10);
              if (page >= 1 && page <= totalPages) {
                handlePageChange(page);
              }
            }}
            InputProps={{ inputProps: { min: 1, max: totalPages } }}
            sx={{
              '& .MuiInputLabel-root': { color: 'white' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'white' },
                '&:hover fieldset': { borderColor: 'white' },
                '&.Mui-focused fieldset': { borderColor: 'white' },
              },
              '& .MuiInputBase-input': { color: 'white' },
            }}
            style={{ maxWidth: '120px' }}
          />
          <span style={{ alignSelf: 'center' }}>
            of {totalPages}
          </span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last
          </Button>
          <FormControl variant="outlined" style={{ minWidth: '120px' }}>
            <InputLabel sx={{ color: 'white' }}>Items per Page</InputLabel>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              label="Items per Page"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '& .MuiSvgIcon-root': { color: 'white' },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </div>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate('/admin-transaction-detail')}
          style={{ marginRight: '10px' }}
        >
          View Transactions
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
      </section>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
